/**
 * FilterPanel — schiebbares Side-Panel mit allen Mitarbeiter-Filtern.
 *
 * Erweiterbar gehalten: alle Optionen kommen aus den Daten (departments,
 * positions, massnahmen, ...). Wenn neue Felder dazukommen, einfach hier
 * eine zusaetzliche Sektion einfuegen und im useEmployeeFilters-Hook
 * zustaendig machen.
 */
import React from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetTrigger,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Filter, RotateCcw } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { SearchableMultiSelect } from './SearchableMultiSelect';
import { OrgUnitFilter } from '../../components/OrgUnitFilter';
import { empQualNames, empTrainingTitles } from './useEmployeeFilters';

const MONTHS = [
  { v: 1, l: 'Januar' }, { v: 2, l: 'Februar' }, { v: 3, l: 'März' },
  { v: 4, l: 'April' }, { v: 5, l: 'Mai' }, { v: 6, l: 'Juni' },
  { v: 7, l: 'Juli' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'Oktober' }, { v: 11, l: 'November' }, { v: 12, l: 'Dezember' },
];

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b, 'de'));

export const FilterPanel = ({
  open, onOpenChange,
  filters, onPatch, onReset,
  employees = [], locations = [], branches = [], orgTree = [], orgLevels = [], orgUnitCounts = null, massnahmen = [],
  activeCount,
}) => {
  // Optionen dynamisch aus Daten ableiten.
  // Maßnahme/Abteilung: kombinierte Werte ("BaE, BvB") in einzelne Optionen splitten,
  // damit jede Maßnahme separat auswählbar ist (nicht nur im Verbund).
  const departments = uniq(
    employees.flatMap((e) => (e.department || '').split(/[,;]/).map((s) => s.trim()))
  );
  const positions = uniq(
    employees.flatMap((e) => [
      ...(e.position ? [e.position] : []),
      ...((e.position_list || []).filter(Boolean)),
    ])
  );
  const employmentTypes = uniq(employees.map((e) => e.employment_type || ''));
  const qualifications = uniq(employees.flatMap((e) => empQualNames(e)));
  const trainings = uniq(employees.flatMap((e) => empTrainingTitles(e)));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" className="rounded-full h-10" data-testid="open-filter-panel">
          <Filter className="mr-2 h-4 w-4" />
          Filter
          {activeCount > 0 && (
            <Badge variant="secondary" className="ml-2 h-5 min-w-5 px-1.5 rounded-full">{activeCount}</Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        className="w-full sm:max-w-md overflow-y-auto"
        side="right"
        data-testid="filter-panel"
      >
        <SheetHeader>
          <SheetTitle>Mitarbeiter filtern</SheetTitle>
          <SheetDescription>
            Felder mit vielen Auswahl-Optionen haben eine eingebaute Suche.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 py-4">
          {/* Organisationseinheit (Baum) + Status */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <OrgUnitFilter
                tree={orgTree}
                levels={orgLevels}
                counts={orgUnitCounts}
                value={filters.org_unit || 'all'}
                onChange={(v) => onPatch({ org_unit: v })}
                label="Organisationseinheit"
                testId="filter-org-unit"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={filters.status || 'all'} onValueChange={(v) => onPatch({ status: v })}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="inactive">Inaktiv</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Befristung</Label>
                <Select value={filters.temporary || 'all'} onValueChange={(v) => onPatch({ temporary: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="no">Unbefristet</SelectItem>
                    <SelectItem value="yes">Befristet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Personalstatus</Label>
                <Select value={filters.onboarding || 'all'} onValueChange={(v) => onPatch({ onboarding: v })}>
                  <SelectTrigger data-testid="filter-onboarding"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="anwaerter">Anwärter</SelectItem>
                    <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Abteilung / Position / Beschäftigung */}
          <div className="space-y-3 border-t pt-4">
            <div className="space-y-1.5">
              <Label>Maßnahme/Abteilung</Label>
              <SearchableMultiSelect
                options={departments.map((d) => ({ value: d, label: d }))}
                selected={filters.departments || []}
                onChange={(v) => onPatch({ departments: v })}
                placeholder="Alle Maßnahmen/Abteilungen"
                searchPlaceholder="Maßnahme/Abteilung suchen..."
                triggerTestId="filter-departments"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Einsatz als</Label>
              <SearchableMultiSelect
                options={positions.map((p) => ({ value: p, label: p }))}
                selected={filters.positions || []}
                onChange={(v) => onPatch({ positions: v })}
                placeholder="Alle Tätigkeiten"
                searchPlaceholder="Tätigkeit suchen..."
                triggerTestId="filter-positions"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Beschäftigungsart</Label>
              <SearchableMultiSelect
                options={employmentTypes.map((t) => ({ value: t, label: t }))}
                selected={filters.employment_types || []}
                onChange={(v) => onPatch({ employment_types: v })}
                placeholder="Alle Beschäftigungsarten"
                searchPlaceholder="Suchen..."
              />
            </div>
          </div>

          {/* Qualifikation / Weiterbildung */}
          <div className="space-y-3 border-t pt-4">
            <div className="space-y-1.5">
              <Label>Qualifikation</Label>
              <SearchableMultiSelect
                options={qualifications.map((q) => ({ value: q, label: q }))}
                selected={filters.qualifications || []}
                onChange={(v) => onPatch({ qualifications: v })}
                placeholder="Alle Qualifikationen"
                searchPlaceholder="Qualifikation suchen..."
                triggerTestId="filter-qualifications"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Weiterbildung</Label>
              <SearchableMultiSelect
                options={trainings.map((t) => ({ value: t, label: t }))}
                selected={filters.trainings || []}
                onChange={(v) => onPatch({ trainings: v })}
                placeholder="Alle Weiterbildungen"
                searchPlaceholder="Weiterbildung suchen..."
                triggerTestId="filter-trainings"
              />
            </div>
          </div>

          {/* Maßnahmen-basiert auf Schichten gibt es hier bewusst NICHT —
              das Mitarbeiter-Feld "Maßnahme/Abteilung" ist oben integriert. */}

          {/* Wohnort + Geburtstag */}
          <div className="space-y-3 border-t pt-4">
            <div className="space-y-1.5">
              <Label htmlFor="filter-city">Wohnort (enthält)</Label>
              <Input
                id="filter-city"
                value={filters.city || ''}
                onChange={(e) => onPatch({ city: e.target.value })}
                placeholder="z.B. Berlin"
                data-testid="filter-city"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Geburtsmonat</Label>
              <SearchableMultiSelect
                options={MONTHS.map((m) => ({ value: m.v, label: m.l }))}
                selected={filters.birth_months || []}
                onChange={(v) => onPatch({ birth_months: v })}
                placeholder="Alle Monate"
                searchPlaceholder="Monat..."
              />
            </div>

            <div className="space-y-1.5">
              <Label>Geburtstag-Schnellfilter</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: null, l: 'Aus' },
                  { v: 'week', l: 'Diese Woche' },
                  { v: 'month', l: 'Dieser Monat' },
                  { v: '90d', l: 'Nächste 90 Tage' },
                ].map((opt) => (
                  <button
                    key={opt.v || 'off'}
                    type="button"
                    onClick={() => onPatch({ birthday_window: opt.v })}
                    className={`text-xs rounded-full border px-3 py-1 transition-colors ${
                      (filters.birthday_window || null) === opt.v
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="border-t pt-4 gap-2">
          <Button variant="outline" onClick={onReset} className="w-full sm:w-auto">
            <RotateCcw className="mr-2 h-4 w-4" />
            Alle zurücksetzen
          </Button>
          <Button onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Anwenden
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default FilterPanel;
